import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Send, Download, Receipt, CheckCircle, Clock, FileText, RefreshCw, Share2, Mail, MessageSquare, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './lead-inv.css';
import {
  getInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  updateInvoiceStatus,
  sendInvoice,
  markInvoiceAsPaid,
  getQuotes,
  getSystemConfig,
  uploadInvoicePdf,
  getInvoicePdfLink
} from '../../api';

const INVOICE_STATUS_COLORS = {
  'Paid': '#10B981',
  'Part Paid': '#F59E0B',
  'Partially Paid': '#F59E0B',
  'Unpaid': '#EF4444',
  'Overdue': '#DC2626',
  'Sent': '#3B82F6',
  'Draft': '#6B7280'
};

const INVOICE_STATUS_ICONS = {
  'Paid': '✅',
  'Part Paid': '💳',
  'Partially Paid': '💳',
  'Unpaid': '⏳',
  'Overdue': '⚠️',
  'Sent': '✉️',
  'Draft': '📄'
};

const formatNum = (num) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(num || 0);

const formatDatePDF = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_) {
    return 'N/A';
  }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_) {
    return 'N/A';
  }
};

// Helper function to convert hex to RGB
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [128, 128, 128];
}

function InvoicesTab({ leadId, leadName, leadPhone = '', leadEmail = '', userRole = 'Owner', onClose }) {
  const [invoices, setInvoices] = useState([]);
  const [approvedQuotes, setApprovedQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // PDF Viewer State
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Share Modal State
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareInvoiceItem, setShareInvoiceItem] = useState(null);
  const [sharePhone, setSharePhone] = useState(leadPhone || '');
  const [shareEmail, setShareEmail] = useState(leadEmail || '');

  // Company Config State
  const [companyConfig, setCompanyConfig] = useState({
    name: 'Your Company Name',
    address: '123 Business Street, City, State 12345',
    phone: '+91 9876543210',
    email: 'info@yourcompany.com',
    gst: 'GSTIN: 22ABCDE1234F1Z5',
    website: '',
    preparedBy: 'Finance Team',
    logo: '',
    logoFormat: 'PNG'
  });

  const isOwner = userRole === 'Owner' || userRole === 'owner';
  const isClient = userRole === 'Client' || userRole === 'client';

  // Form state for create/edit
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    quoteId: '',
    quoteNumber: '',
    campaignId: '',
    customerId: '',
    leadId: leadId || '',
    leadName: leadName || '',
    project: '',
    amount: 0,
    status: 'Draft',
    dueDate: '',
    issuedDate: new Date().toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unitPrice: 0, amount: 0, tax: 18 }],
    paymentInstructions: '',
    termsAndConditions: '',
    discount: 0,
    discountType: 'percentage',
    taxType: 'GST',
    taxRate: 18
  });

  const [isEditing, setIsEditing] = useState(false);

  // Fetch company config on mount
  useEffect(() => {
    const fetchCompanyConfig = async () => {
      try {
        const response = await getSystemConfig();
        console.log('config', response);
        const configData = response?.data;
        if (configData) {
          setCompanyConfig({
            ...companyConfig,
            address: `${configData?.onboarding?.address?.country || ''}, ${configData?.onboarding?.address?.state || ''}, ${configData?.onboarding?.address?.city || ''}, ${configData?.onboarding?.address?.zip || ''}`,
            email: configData?.onboarding?.email || companyConfig.email,
            gst: configData?.onboarding?.businessData?.registrationNumber || companyConfig.gst,
            logo: configData?.logo || companyConfig.logo,
            name: configData?.tenantName || companyConfig.name,
            phone: configData?.onboarding?.phone || companyConfig.phone,
            website: configData?.onboarding?.businessData?.website || companyConfig.website,
          });
        }
      } catch (error) {
        console.error('Failed to fetch company config:', error);
      }
    };
    fetchCompanyConfig();
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchApprovedQuotes();
  }, [leadId]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await getInvoices(leadId);
      if (response && response.data) {
        setInvoices(Array.isArray(response.data) ? response.data : []);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Fetch invoices error:', error);
      toast.error('Failed to fetch invoices from server');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedQuotes = async () => {
    if (!leadId) return;
    try {
      const response = await getQuotes(leadId);
      if (response && response.data) {
        const quotes = Array.isArray(response.data) ? response.data : [];
        const approved = quotes.filter(q => q.status === 'Approved');
        setApprovedQuotes(approved);
      }
    } catch (error) {
      console.error('Fetch approved quotes error:', error);
    }
  };

  const handleSelectQuoteToImport = (quoteId) => {
    setSelectedQuoteId(quoteId);
    if (!quoteId) return;

    const quote = approvedQuotes.find(q => (q._id || q.id) === quoteId);
    if (quote) {
      const grandTotalVal = Number(quote.grandTotal !== undefined ? quote.grandTotal : (quote.amount || 0));
      const rawItems = Array.isArray(quote.items) && quote.items.length > 0
        ? quote.items.map(item => ({
            description: item.description || '',
            quantity: Number(item.quantity || item.qty || 1),
            unitPrice: Number(item.unitPrice || item.rate || 0),
            amount: Number(item.quantity || 1) * Number(item.unitPrice || 0),
            tax: Number(item.tax || 18),
            total: Number(item.total || 0)
          }))
        : [{ description: quote.projectService || quote.quoteTitle || 'Project Item', quantity: 1, unitPrice: grandTotalVal, amount: grandTotalVal, tax: 18 }];

      setFormData(prev => ({
        ...prev,
        quoteId: quote._id || quote.id,
        quoteNumber: quote.quoteNumber || '',
        campaignId: quote.campaignId || '',
        customerId: quote.customerId || '',
        project: quote.projectService || quote.quoteTitle || prev.project,
        amount: grandTotalVal,
        items: rawItems,
        discount: Number(quote.discountValue || 0),
        discountType: quote.discountType || 'percentage',
        termsAndConditions: quote.termsAndConditions || prev.termsAndConditions
      }));
      toast.info(`Imported details from Approved Quote ${quote.quoteNumber}`);
    }
  };

  // ============ PROFESSIONAL INVOICE PDF GENERATION ============
  const generateProfessionalInvoicePDF = (inv) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    let y = margin;

    // Colors
    const primaryColor = [31, 41, 55];
    const accentColor = [79, 70, 229];
    const lightGray = [248, 249, 250];
    const mediumGray = [107, 114, 128];
    const darkGray = [55, 65, 81];
    const white = [255, 255, 255];
    const borderColor = [220, 220, 220];
    const successColor = [16, 185, 129];
    const warningColor = [245, 158, 11];
    const dangerColor = [239, 68, 68];

    // Helper function for drawing lines
    const drawLine = (x1, y1, x2, y2, color = borderColor, width = 0.2) => {
      doc.setDrawColor(...color);
      doc.setLineWidth(width);
      doc.line(x1, y1, x2, y2);
    };

    // ============ HEADER SECTION ============
    const logoBoxX = margin;
    const logoBoxY = y;
    const logoBoxW = 34;
    const logoBoxH = 24;
    const textBlockX = logoBoxX + logoBoxW + 8;

    // Logo
    if (companyConfig.logo) {
      try {
        doc.addImage(
          companyConfig.logo,
          companyConfig.logoFormat || 'PNG',
          logoBoxX,
          logoBoxY,
          logoBoxW,
          logoBoxH,
          undefined,
          'FAST'
        );
      } catch (e) {
        console.error('Failed to render logo in PDF:', e);
      }
    } else {
      // Dashed placeholder box
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.4);
      if (typeof doc.setLineDashPattern === 'function') {
        doc.setLineDashPattern([1.5, 1.5], 0);
      }
      doc.roundedRect(logoBoxX, logoBoxY, logoBoxW, logoBoxH, 1.5, 1.5, 'S');
      if (typeof doc.setLineDashPattern === 'function') {
        doc.setLineDashPattern([], 0);
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...mediumGray);
      doc.text('YOUR LOGO', logoBoxX + logoBoxW / 2, logoBoxY + logoBoxH / 2 - 1, { align: 'center' });
      doc.setFontSize(5.5);
      doc.text('(add manually)', logoBoxX + logoBoxW / 2, logoBoxY + logoBoxH / 2 + 4, { align: 'center' });
    }

    // Company name & details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...primaryColor);
    doc.text(companyConfig.name, textBlockX, logoBoxY + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mediumGray);
    const companyLines = doc.splitTextToSize(companyConfig.address, 78);
    let compY = logoBoxY + 11.5;
    companyLines.slice(0, 2).forEach((line) => {
      doc.text(line, textBlockX, compY);
      compY += 4;
    });
    doc.text(`Ph: ${companyConfig.phone}`, textBlockX, compY);
    compY += 4;
    doc.text(`${companyConfig.email}  |  ${companyConfig.website}`, textBlockX, compY);

    // Invoice title block (right-aligned)
    const invNum = inv.invoiceNumber || inv.id || 'INV-000';
    const status = inv.status || 'Draft';
    const statusColor = INVOICE_STATUS_COLORS[status] || '#6B7280';

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...accentColor);
    doc.text('INVOICE', pageWidth - margin, logoBoxY + 6, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...mediumGray);
    doc.text(`# ${invNum}`, pageWidth - margin, logoBoxY + 12, { align: 'right' });

    // Status badge
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...white);
    doc.setFillColor(...hexToRgb(statusColor));
    const badgeW = 30;
    doc.roundedRect(pageWidth - margin - badgeW, logoBoxY + 15, badgeW, 7, 1, 1, 'F');
    doc.text(status, pageWidth - margin - badgeW / 2, logoBoxY + 19.5, { align: 'center' });

    // GST line
    const headerBottom = logoBoxY + logoBoxH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...mediumGray);
    doc.text(`${companyConfig.gst}   |   Prepared by: ${companyConfig.preparedBy}`, margin, headerBottom + 4);

    y = headerBottom + 9;

    // Decorative line
    drawLine(margin, y, pageWidth - margin, y, accentColor, 0.5);
    y += 10;

    // ============ INVOICE INFO GRID ============
    const infoY = y;
    const col1 = margin;
    const col2 = margin + 55;
    const col3 = margin + 110;
    const rowHeight = 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...darkGray);

    // Row 1: Issue Date
    doc.text('Issue Date:', col1, infoY);
    doc.text(formatDateLong(inv.issuedDate || inv.createdAt || Date.now()), col2, infoY);
    
    // Row 2: Due Date
    doc.text('Due Date:', col1, infoY + rowHeight);
    doc.text(formatDateLong(inv.dueDate), col2, infoY + rowHeight);
    
    // Row 3: Customer ID
    doc.text('Customer ID:', col1, infoY + rowHeight * 2);
    doc.text(inv.leadId || leadId || 'LD-0000', col2, infoY + rowHeight * 2);
    
    // Row 4: Quote Reference
    if (inv.quoteNumber) {
      doc.text('Quote Ref:', col3, infoY);
      doc.text(inv.quoteNumber, col3 + 30, infoY);
    }
    
    // Row 5: Payment Status
    doc.text('Payment Status:', col3, infoY + rowHeight);
    const paymentStatus = inv.paymentStatus || status;
    const psColor = paymentStatus === 'Paid' ? successColor : 
                    paymentStatus === 'Partially Paid' ? warningColor : dangerColor;
    doc.setTextColor(...psColor);
    doc.text(paymentStatus, col3 + 30, infoY + rowHeight);
    doc.setTextColor(...darkGray);

    y = infoY + rowHeight * 3 + 10;

    // ============ DIVIDER ============
    drawLine(margin, y, pageWidth - margin, y, borderColor, 0.3);
    y += 8;

    // ============ BILL TO SECTION ============
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...primaryColor);
    doc.text('BILL TO', margin, y);
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkGray);
    
    // Customer Name
    doc.text(inv.leadName || leadName || 'Valued Customer', margin, y);
    y += 5;

    // Project / Service
    doc.setFontSize(9);
    doc.setTextColor(...mediumGray);
    doc.text(`Project / Service: ${inv.project || 'N/A'}`, margin, y);
    y += 5;

    // Customer Contact
    if (leadPhone || leadEmail) {
      y += 2;
      doc.setFontSize(9);
      doc.setTextColor(...mediumGray);
      let contactText = '';
      if (leadPhone) contactText += `Phone: ${leadPhone}`;
      if (leadEmail) contactText += contactText ? ` | Email: ${leadEmail}` : `Email: ${leadEmail}`;
      if (contactText) doc.text(contactText, margin, y);
      y += 5;
    }

    y += 5;
    drawLine(margin, y, pageWidth - margin, y, borderColor, 0.3);
    y += 10;

    // ============ ITEMS TABLE ============
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.text('Items / Services', margin, y);
    y += 6;

    const rawItems = Array.isArray(inv.items) && inv.items.length > 0
      ? inv.items
      : [{ description: inv.project || 'Project Item', quantity: 1, unitPrice: inv.amount || 0, tax: inv.taxRate || 18 }];

    // Prepare table data
    const tableHeaders = [
      { title: '#', align: 'center', width: 10 },
      { title: 'Description', align: 'left', width: 65 },
      { title: 'Qty', align: 'center', width: 16 },
      { title: 'Unit Price', align: 'right', width: 28 },
      { title: 'Tax %', align: 'center', width: 16 },
      { title: 'Total', align: 'right', width: 35 }
    ];

    const tableBody = rawItems.map((item, index) => {
      const qty = Number(item.quantity || 1);
      const rate = Number(item.unitPrice || item.amount || 0);
      const tax = Number(item.tax || inv.taxRate || 18);
      const lineBeforeTax = qty * rate;
      const lineTotal = lineBeforeTax * (1 + tax / 100);

      return [
        String(index + 1),
        item.description || 'Item Description',
        String(qty),
        `Rs. ${formatNum(rate)}`,
        `${tax}%`,
        `Rs. ${formatNum(lineTotal)}`
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [tableHeaders.map(h => h.title)],
      body: tableBody,
      headStyles: {
        fillColor: [245, 245, 250],
        textColor: darkGray,
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: borderColor
      },
      bodyStyles: {
        textColor: darkGray,
        fontSize: 8,
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: borderColor
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 65, halign: 'left' },
        2: { cellWidth: 16, halign: 'center' },
        3: { cellWidth: 28, halign: 'right' },
        4: { cellWidth: 16, halign: 'center' },
        5: { cellWidth: 35, halign: 'right' }
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      margin: { left: margin, right: margin },
      tableWidth: pageWidth - (margin * 2),
      styles: {
        fontSize: 8,
        cellPadding: 2.5
      }
    });

    y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : y + 40;

    // ============ SUMMARY SECTION ============
    const subtotal = rawItems.reduce((sum, item) => sum + (Number(item.quantity || 1) * Number(item.unitPrice || item.amount || 0)), 0);
    const discVal = Number(inv.discount || 0);
    const discAmt = inv.discountType === 'percentage' ? (subtotal * discVal) / 100 : discVal;
    const taxAmt = Math.max(0, subtotal - discAmt) * (Number(inv.taxRate || 18) / 100);
    const grandTotal = Number(inv.amount || (subtotal - discAmt + taxAmt));
    const paidAmt = Number(inv.paidAmount || 0);
    const balanceDue = Math.max(0, grandTotal - paidAmt);

    // Summary box on the right
    const summaryWidth = 80;
    const summaryX = pageWidth - margin - summaryWidth;
    const summaryY = y;

    // Box background
    doc.setFillColor(...lightGray);
    doc.roundedRect(summaryX, summaryY, summaryWidth, 55, 2, 2, 'F');
    doc.setDrawColor(...borderColor);
    doc.roundedRect(summaryX, summaryY, summaryWidth, 55, 2, 2, 'S');

    const renderSummaryRow = (label, value, yPos, isBold = false, isAccent = false, fontSize = 9) => {
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(...(isAccent ? accentColor : darkGray));
      doc.text(label, summaryX + 6, yPos);
      doc.text(value, summaryX + summaryWidth - 6, yPos, { align: 'right' });
    };

    let sY = summaryY + 6;
    renderSummaryRow('Subtotal:', `Rs. ${formatNum(subtotal)}`, sY);
    sY += 6;
    
    if (discAmt > 0) {
      renderSummaryRow('Discount:', `- Rs. ${formatNum(discAmt)}`, sY);
      sY += 6;
    }
    
    renderSummaryRow(`Tax (${inv.taxRate || 18}%):`, `Rs. ${formatNum(taxAmt)}`, sY);
    sY += 6;

    // Divider
    doc.setDrawColor(...borderColor);
    doc.line(summaryX + 6, sY, summaryX + summaryWidth - 6, sY);
    sY += 5;

    renderSummaryRow('Grand Total:', `Rs. ${formatNum(grandTotal)}`, sY, true, true, 11);
    sY += 6;

    if (paidAmt > 0) {
      renderSummaryRow('Paid Amount:', `Rs. ${formatNum(paidAmt)}`, sY);
      sY += 6;
      renderSummaryRow('Balance Due:', `Rs. ${formatNum(balanceDue)}`, sY, true, false, 10);
    }

    y = summaryY + 65;

    // ============ PAYMENT INSTRUCTIONS & TERMS ============
    if (inv.paymentInstructions || inv.termsAndConditions) {
      const hasPayment = inv.paymentInstructions && inv.paymentInstructions.trim();
      const hasTerms = inv.termsAndConditions && inv.termsAndConditions.trim();

      if (hasPayment) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...primaryColor);
        doc.text('Payment Instructions', margin, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...mediumGray);
        const splitPayment = doc.splitTextToSize(inv.paymentInstructions, pageWidth - (margin * 2));
        doc.text(splitPayment, margin, y);
        y += splitPayment.length * 4 + 5;
      }

      if (hasTerms) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...primaryColor);
        doc.text('Terms & Conditions', margin, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...mediumGray);
        const splitTerms = doc.splitTextToSize(inv.termsAndConditions, pageWidth - (margin * 2));
        doc.text(splitTerms, margin, y);
        y += splitTerms.length * 4 + 5;
      }
    }

    // ============ SIGNATURE SECTION ============
    const signY = Math.max(y + 10, 230);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...primaryColor);
    doc.text('Customer Acceptance', margin, signY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mediumGray);
    doc.text('By signing below, you acknowledge acceptance and receipt of this invoice.', margin, signY + 5);

    // Signature lines
    const signLineY = signY + 12;
    
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.5);
    doc.line(margin, signLineY, margin + 55, signLineY);
    doc.setFontSize(7);
    doc.setTextColor(...mediumGray);
    doc.text('Signature', margin, signLineY + 4);

    doc.line(margin + 65, signLineY, margin + 95, signLineY);
    doc.text('Date', margin + 65, signLineY + 4);

    doc.line(margin + 105, signLineY, margin + 165, signLineY);
    doc.text('Print Name', margin + 105, signLineY + 4);

    // ============ FOOTER ============
    const footerY = pageHeight - 12;
    doc.setFillColor(...lightGray);
    doc.rect(0, footerY, pageWidth, 12, 'F');
    doc.setDrawColor(...borderColor);
    doc.line(0, footerY, pageWidth, footerY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mediumGray);
    doc.text(`Invoice ${invNum} • Thank you for your business!`, pageWidth / 2, footerY + 6, { align: 'center' });
    doc.text(companyConfig.website, pageWidth - margin, footerY + 6, { align: 'right' });

    return doc;
  };

  // ============ PDF GENERATION AND STORAGE ============
  const handleGenerateAndStorePDF = async (inv) => {
    setGeneratingPdf(true);
    try {
      const doc = generateProfessionalInvoicePDF(inv);
      const pdfBlob = doc.output('blob');
      
      const formData = new FormData();
      const fileName = `${inv.invoiceNumber || inv.id || 'invoice'}.pdf`;
      formData.append('file1', pdfBlob, fileName);
      
      const invId = inv._id || inv.id;
      const response = await uploadInvoicePdf(invId, formData);
      
      if (response.success) {
        toast.success('Invoice PDF generated and stored successfully!');
        const linkResponse = await getInvoicePdfLink(invId);
        if (linkResponse.success && linkResponse.pdfUrl) {
          setPdfUrl(linkResponse.pdfUrl);
          setShowPdfViewer(true);
        }
        await fetchInvoices();
      } else {
        toast.error('Failed to store PDF');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(error?.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleViewPDF = async (inv) => {
    try {
      const invId = inv._id || inv.id;
      const response = await getInvoicePdfLink(invId);
      
      if (response.success && response.pdfUrl) {
        setPdfUrl(response.pdfUrl);
        setShowPdfViewer(true);
      } else {
        await handleGenerateAndStorePDF(inv);
      }
    } catch (error) {
      console.error('Get PDF error:', error);
      if (error?.response?.status === 404) {
        await handleGenerateAndStorePDF(inv);
      } else {
        toast.error('Failed to load PDF');
      }
    }
  };

  const getPdfShareLink = async (inv) => {
    try {
      const invId = inv._id || inv.id;
      const response = await getInvoicePdfLink(invId);
      if (response.success && response.pdfUrl) {
        return response.pdfUrl;
      }
      return null;
    } catch (error) {
      console.error('Get PDF link error:', error);
      return null;
    }
  };

  // ============ SHARING FUNCTIONS ============
  const handleOpenShareModal = (invoice) => {
    setShareInvoiceItem(invoice);
    setSharePhone(leadPhone || '');
    setShareEmail(leadEmail || '');
    setShowShareModal(true);
  };

  const handleShareWhatsApp = async () => {
    if (!shareInvoiceItem) return;
    const inv = shareInvoiceItem;
    const cust = inv.leadName || leadName || 'Customer';
    const invNum = inv.invoiceNumber || inv.id || 'Invoice';
    const comp = companyConfig.name;

    let pdfLink = '';
    try {
      const link = await getPdfShareLink(inv);
      if (link) {
        pdfLink = `\n\n📄 View/Download PDF: ${link}`;
      }
    } catch (e) {
      console.log('No PDF available for sharing');
    }

    const cleanPhone = sharePhone.replace(/[^\d+]/g, '');

    const messageText =
      `Hello ${cust},\n` +
      `Thank you for your business.\n\n` +
      `Please find your invoice ${invNum} details. Kindly review it and let us know if you have any questions.${pdfLink}\n\n` +
      `Thank you,\n` +
      `${comp}`;

    const encodedText = encodeURIComponent(messageText);
    const whatsappUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedText}`
      : `https://api.whatsapp.com/send?text=${encodedText}`;

    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp to share invoice...');
    setShowShareModal(false);
  };

  const getEmailContent = async (inv) => {
    const invNum = inv.invoiceNumber || inv.id || 'Invoice';
    const cust = inv.leadName || leadName || 'Customer';
    const comp = companyConfig.name;
    const totalAmt = formatNum(inv.amount || 0);

    let pdfLink = '';
    try {
      const link = await getPdfShareLink(inv);
      if (link) {
        pdfLink = `\n\nYou can view/download the PDF here: ${link}`;
      }
    } catch (e) {
      console.log('No PDF available for email');
    }

    const subject = `Invoice ${invNum} from ${comp}`;
    const body =
      `Dear ${cust},\n\n` +
      `Please find your invoice ${invNum} (Total: Rs. ${totalAmt}) details attached.${pdfLink}\n\n` +
      `Kindly review it and let us know if you have any questions or require any assistance.\n\n` +
      `Payment is due as specified on the invoice.\n\n` +
      `Regards,\n` +
      `${comp}`;

    return { subject, body };
  };

  const handleShareGmailWeb = async () => {
    if (!shareInvoiceItem) return;
    const { subject, body } = await getEmailContent(shareInvoiceItem);

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(shareEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
    toast.success('Opening Gmail Web Composer...');
    setShowShareModal(false);
  };

  const handleShareMailto = async () => {
    if (!shareInvoiceItem) return;
    const { subject, body } = await getEmailContent(shareInvoiceItem);

    const mailtoUrl = `mailto:${shareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    toast.success('Opening default Mail Client...');
    setShowShareModal(false);
  };

  // ============ CRUD OPERATIONS ============
  const handleCreateInvoice = async () => {
    if (!formData.project || !formData.dueDate) {
      toast.error('Please fill in all required fields (Project/Service Name, Due Date)');
      return;
    }

    const invalidItems = formData.items.some(item => !item.description);
    if (invalidItems) {
      toast.error('Please ensure all line items have a description');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        invoiceNumber: formData.invoiceNumber || undefined,
        quoteId: formData.quoteId || undefined,
        quoteNumber: formData.quoteNumber || undefined,
        campaignId: formData.campaignId || undefined,
        customerId: formData.customerId || undefined,
        leadId: formData.leadId || leadId || 'N/A',
        leadName: formData.leadName || leadName || 'Customer',
        project: formData.project,
        amount: Number(totals.total || formData.amount || 0),
        status: formData.status || 'Draft',
        dueDate: formData.dueDate,
        issuedDate: formData.issuedDate || new Date().toISOString().split('T')[0],
        items: formData.items.map(item => ({
          description: item.description,
          quantity: Number(item.quantity || 1),
          unitPrice: Number(item.unitPrice || 0),
          amount: Number(item.amount || (Number(item.quantity || 1) * Number(item.unitPrice || 0))),
          tax: Number(item.tax || 18)
        })),
        paymentInstructions: formData.paymentInstructions,
        termsAndConditions: formData.termsAndConditions,
        discount: Number(formData.discount) || 0,
        discountType: formData.discountType || 'percentage',
        taxType: formData.taxType || 'GST',
        taxRate: Number(formData.taxRate) || 18
      };

      const response = await createInvoice(payload);
      toast.success(response?.message || 'Invoice created successfully');
      resetForm();
      setShowCreateModal(false);
      await fetchInvoices();
    } catch (error) {
      console.error('Create invoice error:', error);
      toast.error(error?.response?.data?.message || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice) return;

    if (['Paid', 'Part Paid', 'Partially Paid'].includes(selectedInvoice.status) && !formData.status) {
      toast.error('Invoices with recorded payments cannot be modified.');
      return;
    }

    setSubmitting(true);
    try {
      const targetId = selectedInvoice._id || selectedInvoice.id;
      const payload = {
        project: formData.project,
        amount: Number(totals.total || formData.amount || 0),
        status: formData.status,
        dueDate: formData.dueDate,
        issuedDate: formData.issuedDate,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: Number(item.quantity || 1),
          unitPrice: Number(item.unitPrice || 0),
          amount: Number(item.amount || (Number(item.quantity || 1) * Number(item.unitPrice || 0))),
          tax: Number(item.tax || 18)
        })),
        paymentInstructions: formData.paymentInstructions,
        termsAndConditions: formData.termsAndConditions,
        discount: Number(formData.discount) || 0,
        discountType: formData.discountType || 'percentage',
        taxType: formData.taxType || 'GST',
        taxRate: Number(formData.taxRate) || 18
      };

      const response = await updateInvoice(targetId, payload);
      toast.success(response?.message || 'Invoice updated successfully');
      resetForm();
      setShowCreateModal(false);
      setSelectedInvoice(null);
      setIsEditing(false);
      await fetchInvoices();
    } catch (error) {
      console.error('Update invoice error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId, status) => {
    if (status !== 'Draft') {
      toast.error(`Only invoices in 'Draft' status can be deleted. Current status: '${status}'`);
      return;
    }
    if (!window.confirm('Are you sure you want to delete this draft invoice?')) return;

    try {
      await deleteInvoice(invoiceId);
      toast.success('Invoice deleted successfully');
      await fetchInvoices();
    } catch (error) {
      console.error('Delete invoice error:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const handleSendInvoiceAction = async (invoiceId) => {
    try {
      await sendInvoice(invoiceId);
      toast.success('Invoice marked as Sent');
      await fetchInvoices();
    } catch (error) {
      console.error('Send invoice error:', error);
      toast.error(error?.response?.data?.message || 'Failed to send invoice');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await updateInvoiceStatus(invoiceId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      await fetchInvoices();
      if (selectedInvoice) {
        setSelectedInvoice(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (error) {
      console.error('Status change error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleMarkAsPaidAction = async (invoiceId) => {
    try {
      await markInvoiceAsPaid(invoiceId);
      toast.success('Invoice marked as paid');
      await fetchInvoices();
    } catch (error) {
      console.error('Mark paid error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update payment status');
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleEditInvoice = (invoice) => {
    if (invoice.status === 'Paid' || invoice.paymentStatus === 'Paid') {
      toast.error('Paid invoices are finalized and cannot be edited.');
      return;
    }
    setSelectedInvoice(invoice);
    const rawItems = Array.isArray(invoice.items) && invoice.items.length > 0
      ? invoice.items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0,
          amount: item.amount || (item.quantity || 1) * (item.unitPrice || 0),
          tax: item.tax || 18
        }))
      : [{ description: '', quantity: 1, unitPrice: 0, amount: 0, tax: 18 }];

    setFormData({
      invoiceNumber: invoice.invoiceNumber || '',
      quoteId: invoice.quoteId || '',
      quoteNumber: invoice.quoteNumber || '',
      campaignId: invoice.campaignId || '',
      customerId: invoice.customerId || '',
      leadId: invoice.leadId || leadId || '',
      leadName: invoice.leadName || leadName || '',
      project: invoice.project || '',
      amount: invoice.amount || 0,
      status: invoice.status || 'Draft',
      dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '',
      issuedDate: invoice.issuedDate ? new Date(invoice.issuedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      items: rawItems,
      paymentInstructions: invoice.paymentInstructions || '',
      termsAndConditions: invoice.termsAndConditions || '',
      discount: invoice.discount || 0,
      discountType: invoice.discountType || 'percentage',
      taxType: invoice.taxType || 'GST',
      taxRate: invoice.taxRate || 18
    });
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      invoiceNumber: '',
      quoteId: '',
      quoteNumber: '',
      campaignId: '',
      customerId: '',
      leadId: leadId || '',
      leadName: leadName || '',
      project: '',
      amount: 0,
      status: 'Draft',
      dueDate: '',
      issuedDate: new Date().toISOString().split('T')[0],
      items: [{ description: '', quantity: 1, unitPrice: 0, amount: 0, tax: 18 }],
      paymentInstructions: '',
      termsAndConditions: '',
      discount: 0,
      discountType: 'percentage',
      taxType: 'GST',
      taxRate: 18
    });
    setSelectedQuoteId('');
    setIsEditing(false);
    setSelectedInvoice(null);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unitPrice: 0, amount: 0, tax: prev.taxRate || 18 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) {
      toast.warning('At least one line item is required');
      return;
    }
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = formData.items.map((item, i) => {
      if (i !== index) return item;
      const newItem = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        const qty = Number(field === 'quantity' ? value : newItem.quantity) || 0;
        const price = Number(field === 'unitPrice' ? value : newItem.unitPrice) || 0;
        newItem.amount = qty * price;
      }
      return newItem;
    });
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const amt = item.amount !== undefined && item.amount !== 0
        ? Number(item.amount)
        : (Number(item.quantity || 1) * Number(item.unitPrice || 0));
      return sum + amt;
    }, 0);

    const discountAmount = formData.discountType === 'percentage'
      ? (subtotal * (formData.discount || 0)) / 100
      : (formData.discount || 0);

    const taxableAmount = Math.max(0, subtotal - discountAmount);
    const tax = (taxableAmount * (formData.taxRate || 18)) / 100;
    return {
      subtotal,
      discountAmount,
      tax,
      total: taxableAmount + tax
    };
  };

  const totals = calculateTotal();

  const getStatusBadge = (status) => {
    const color = INVOICE_STATUS_COLORS[status] || '#6B7280';
    const icon = INVOICE_STATUS_ICONS[status] || '📄';
    return (
      <span className="inv-status-badge" style={{ backgroundColor: color + '20', color: color, border: '1px solid ' + color + '40' }}>
        {icon} {status}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const color = paymentStatus === 'Paid' ? '#10B981' : 
                  paymentStatus === 'Partially Paid' ? '#F59E0B' : 
                  paymentStatus === 'Overdue' ? '#DC2626' : '#EF4444';
    const icon = paymentStatus === 'Paid' ? '✅' : 
                 paymentStatus === 'Partially Paid' ? '💳' : 
                 paymentStatus === 'Overdue' ? '⚠️' : '⏳';
    return (
      <span className="inv-status-badge" style={{ backgroundColor: color + '20', color: color, border: '1px solid ' + color + '40' }}>
        {icon} {paymentStatus}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return `Rs. ${formatNum(amount)}`;
  };

  // PDF Viewer Modal Component
  const PdfViewer = () => {
    if (!showPdfViewer || !pdfUrl) return null;

    return (
      <div className="inv-modal-overlay" onClick={() => setShowPdfViewer(false)}>
        <div className="inv-modal-content" style={{ maxWidth: '90%', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
          <div className="inv-modal-header">
            <h3>📄 Invoice PDF</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="inv-btn-secondary"
                onClick={() => window.open(pdfUrl, '_blank')}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <ExternalLink size={16} /> Open in New Tab
              </button>
              <button
                className="inv-btn-secondary"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = 'invoice.pdf';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Download size={16} /> Download
              </button>
              <button
                className="inv-modal-close"
                onClick={() => setShowPdfViewer(false)}
              >
                ×
              </button>
            </div>
          </div>
          <div className="inv-modal-body" style={{ padding: 0, height: '80vh' }}>
            <iframe
              src={pdfUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Invoice PDF Viewer"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="lead-inv-container">
      {/* Header with actions */}
      <div className="inv-header">
        <div className="inv-header-left">
          <h3 className="inv-title">Invoices</h3>
          <span className="inv-count">{invoices.length} invoices</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="inv-btn-secondary"
            onClick={fetchInvoices}
            title="Refresh invoices list"
            style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          {!isClient && (
            <button 
              className="inv-btn-primary"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              <Plus size={18} />
              New Invoice
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="inv-table-container">
        {loading ? (
          <div className="inv-loading">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className="inv-empty-state">
            <Receipt size={48} />
            <p>No invoices found</p>
            <p className="inv-empty-sub">
              {isOwner ? 'Create an invoice or reference an approved quote for this lead' : 'No invoices issued yet'}
            </p>
          </div>
        ) : (
          <table className="inv-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Quote Ref</th>
                <th>Project</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Due Date</th>
                <th style={{ textAlign: 'right', paddingRight: '20px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const invId = invoice._id || invoice.id;
                const isPaid = invoice.status === 'Paid' || invoice.paymentStatus === 'Paid';
                const isDraft = invoice.status === 'Draft';
                const hasPdf = invoice.pdfKey || false;

                return (
                  <tr key={invId}>
                    <td className="inv-invoice-number">
                      {invoice.invoiceNumber}
                      {hasPdf && (
                        <span style={{ fontSize: '10px', color: '#059669', marginLeft: '4px', fontWeight: 600 }}>
                          📄
                        </span>
                      )}
                    </td>
                    <td>{invoice.quoteNumber ? <span style={{ fontSize: '12px', fontWeight: '600', color: '#7C3AED' }}>{invoice.quoteNumber}</span> : '-'}</td>
                    <td>{invoice.project}</td>
                    <td className="inv-amount">{formatCurrency(invoice.amount)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{getPaymentStatusBadge(invoice.paymentStatus || invoice.status)}</td>
                    <td className="inv-date">{invoice.dueDate ? formatDatePDF(invoice.dueDate) : '-'}</td>
                    <td style={{ textAlign: 'right', paddingRight: '16px' }}>
                      <div className="inv-actions" style={{ justifyContent: 'flex-end', gap: '6px' }}>
                        {/* View Action */}
                        <button 
                          className="inv-btn-icon inv-btn-view"
                          onClick={() => handleViewInvoice(invoice)}
                          title="View Invoice"
                        >
                          <Eye size={16} />
                        </button>

                        {/* PDF Action - Generate/View */}
                        <button
                          className="inv-btn-icon"
                          style={{ 
                            color: hasPdf ? '#059669' : '#DC2626', 
                            background: hasPdf ? '#D1FAE5' : '#FEE2E2' 
                          }}
                          onClick={() => handleViewPDF(invoice)}
                          title={hasPdf ? "View PDF" : "Generate PDF"}
                          disabled={generatingPdf}
                        >
                          {generatingPdf ? (
                            <RefreshCw size={16} className="spin" />
                          ) : (
                            <FileText size={16} />
                          )}
                        </button>

                        {/* Share Action */}
                        <button
                          className="inv-btn-icon inv-btn-send"
                          onClick={() => handleOpenShareModal(invoice)}
                          title="Share Invoice"
                        >
                          <Share2 size={16} />
                        </button>

                        {/* Mark Paid Action: Sent/Unpaid only */}
                        {isOwner && !isDraft && !isPaid && (
                          <button 
                            className="inv-btn-icon inv-btn-paid"
                            onClick={() => handleMarkAsPaidAction(invId)}
                            title="Mark as Paid"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}

                        {/* Edit Action: Draft & Sent only */}
                        {isOwner && !isPaid && (
                          <button 
                            className="inv-btn-icon inv-btn-edit"
                            onClick={() => handleEditInvoice(invoice)}
                            title="Edit Invoice"
                          >
                            <Edit size={16} />
                          </button>
                        )}

                        {/* Delete Action: Draft only */}
                        {isOwner && isDraft && (
                          <button 
                            className="inv-btn-icon inv-btn-delete"
                            onClick={() => handleDeleteInvoice(invId, invoice.status)}
                            title="Delete Invoice"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && isOwner && (
        <div className="inv-modal-overlay" onClick={() => {
          if (!submitting) {
            resetForm();
            setShowCreateModal(false);
          }
        }}>
          <div className="inv-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3>{isEditing ? 'Edit Invoice' : 'Create New Invoice'}</h3>
              <button 
                className="inv-modal-close"
                onClick={() => {
                  resetForm();
                  setShowCreateModal(false);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="inv-modal-body">
              {/* Approved Quote Auto-fill Selector */}
              {!isEditing && approvedQuotes.length > 0 && (
                <div className="inv-form-section" style={{ backgroundColor: '#F3F4F6', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4B5563', marginBottom: '6px' }}>
                    🔗 Reference Approved Quote (Optional):
                  </label>
                  <select
                    value={selectedQuoteId}
                    onChange={(e) => handleSelectQuoteToImport(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      color: '#111827',
                      backgroundColor: '#FFFFFF'
                    }}
                  >
                    <option value="">-- Select an Approved Quote --</option>
                    {approvedQuotes.map(q => (
                      <option key={q._id || q.id} value={q._id || q.id}>
                        {q.quoteNumber} - {q.projectService || q.quoteTitle} ({formatCurrency(q.grandTotal || q.amount)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Invoice Details */}
              <div className="inv-form-section">
                <h4>Invoice Details</h4>
                <div className="inv-form-grid">
                  <div className="inv-form-group">
                    <label>Invoice Number</label>
                    <input
                      type="text"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                      placeholder="Auto-generated (e.g. INV-2026-0001)"
                    />
                  </div>
                  <div className="inv-form-group">
                    <label>Issue Date</label>
                    <input
                      type="date"
                      value={formData.issuedDate}
                      onChange={(e) => setFormData({...formData, issuedDate: e.target.value})}
                    />
                  </div>
                  <div className="inv-form-group">
                    <label>Due Date *</label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      required
                    />
                  </div>
                  <div className="inv-form-group">
                    <label>Project / Service *</label>
                    <input
                      type="text"
                      value={formData.project}
                      onChange={(e) => setFormData({...formData, project: e.target.value})}
                      placeholder="Enter project name"
                      required
                    />
                  </div>
                  <div className="inv-form-group">
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Sent">Sent</option>
                      <option value="Paid">Paid</option>
                      <option value="Partially Paid">Partially Paid</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  </div>
                  <div className="inv-form-group inv-form-group-full">
                    <label>Customer</label>
                    <input
                      type="text"
                      value={formData.leadName}
                      onChange={(e) => setFormData({...formData, leadName: e.target.value})}
                      placeholder="Customer name"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="inv-form-section">
                <div className="inv-section-header">
                  <h4>Line Items</h4>
                  <button type="button" className="inv-btn-soft" onClick={addItem}>
                    <Plus size={16} /> Add Item
                  </button>
                </div>
                <div className="inv-items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Description *</th>
                        <th style={{width: '90px'}}>Qty</th>
                        <th style={{width: '130px'}}>Unit Rate (₹)</th>
                        <th style={{width: '80px'}}>Tax %</th>
                        <th style={{width: '130px'}}>Total</th>
                        <th style={{width: '40px'}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => {
                        const qty = Number(item.quantity || 0);
                        const rate = Number(item.unitPrice || item.amount || 0);
                        const tax = Number(item.tax || formData.taxRate || 0);
                        const lineBeforeTax = qty * rate;
                        const lineTotal = lineBeforeTax * (1 + tax / 100);

                        return (
                          <tr key={index}>
                            <td>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                placeholder="Item description"
                              />
                            </td>
                            <td>
  <input
    type="number"
    value={item.quantity}
    onChange={(e) =>
      updateItem(
        index,
        'quantity',
        e.target.value === '' ? '' : Number(e.target.value)
      )
    }
    onBlur={(e) => {
      if (e.target.value === '') {
        updateItem(index, 'quantity', 0);
      }
    }}
    min="1"
  />
</td>
                           <td>
  <input
    type="number"
    value={item.unitPrice !== undefined ? item.unitPrice : item.amount}
    onChange={(e) =>
      updateItem(
        index,
        'unitPrice',
        e.target.value === '' ? '' : Number(e.target.value)
      )
    }
    onBlur={(e) => {
      if (e.target.value === '') {
        updateItem(index, 'unitPrice', 0);
      }
    }}
    min="0"
  />
</td>
                            <td>
  <input
    type="number"
    value={item.tax !== undefined ? item.tax : formData.taxRate}
    onChange={(e) =>
      updateItem(
        index,
        'tax',
        e.target.value === '' ? '' : Number(e.target.value)
      )
    }
    onBlur={(e) => {
      if (e.target.value === '') {
        updateItem(index, 'tax', 0);
      }
    }}
    min="0"
  />
</td>
                            <td className="inv-item-amount">
                              {formatCurrency(lineTotal)}
                            </td>
                            <td>
                              <button 
                                type="button"
                                className="inv-btn-icon inv-btn-delete"
                                onClick={() => removeItem(index)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tax Settings */}
              <div className="inv-form-section">
                <div className="inv-form-grid">
                  <div className="inv-form-group">
                    <label>Tax Type</label>
                    <select
                      value={formData.taxType}
                      onChange={(e) => setFormData({...formData, taxType: e.target.value})}
                    >
                      <option value="GST">GST</option>
                      <option value="VAT">VAT</option>
                      <option value="Sales Tax">Sales Tax</option>
                      <option value="None">None</option>
                    </select>
                  </div>
                  <div className="inv-form-group">
                    <label>Tax Rate (%)</label>
                    <input
  type="number"
  value={formData.taxRate}
  onChange={(e) =>
    setFormData({
      ...formData,
      taxRate: e.target.value === '' ? '' : Number(e.target.value)
    })
  }
  onBlur={(e) => {
    if (e.target.value === '') {
      setFormData({
        ...formData,
        taxRate: 0
      });
    }
  }}
  min="0"
  max="100"
/>
                  </div>
                  <div className="inv-form-group">
                    <label>Discount Value</label>
                    <input
  type="number"
  value={formData.discount}
  onChange={(e) =>
    setFormData({
      ...formData,
      discount: e.target.value === '' ? '' : Number(e.target.value)
    })
  }
  onBlur={(e) => {
    if (e.target.value === '') {
      setFormData({
        ...formData,
        discount: 0
      });
    }
  }}
  min="0"
/>
                  </div>
                  <div className="inv-form-group inv-form-group-full">
                    <label>Payment Instructions</label>
                    <textarea
                      value={formData.paymentInstructions}
                      onChange={(e) => setFormData({...formData, paymentInstructions: e.target.value})}
                      rows="2"
                      placeholder="Enter bank transfer or UPI instructions"
                    />
                  </div>
                  <div className="inv-form-group inv-form-group-full">
                    <label>Terms and Conditions</label>
                    <textarea
                      value={formData.termsAndConditions}
                      onChange={(e) => setFormData({...formData, termsAndConditions: e.target.value})}
                      rows="2"
                      placeholder="Enter invoice terms and conditions"
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="inv-summary">
                <div className="inv-summary-row">
                  <span>Subtotal</span>
                  <span>{formatCurrency(totals.subtotal)}</span>
                </div>
                {formData.discount > 0 && (
                  <div className="inv-summary-row">
                    <span>Discount ({formData.discount}{formData.discountType === 'percentage' ? '%' : ''})</span>
                    <span className="inv-discount">-{formatCurrency(totals.discountAmount)}</span>
                  </div>
                )}
                <div className="inv-summary-row">
                  <span>{formData.taxType || 'Tax'} ({formData.taxRate}%)</span>
                  <span>{formatCurrency(totals.tax)}</span>
                </div>
                <div className="inv-summary-row inv-summary-total">
                  <span>Total</span>
                  <span>{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>

            <div className="inv-modal-footer">
              <button 
                className="inv-btn-secondary"
                disabled={submitting}
                onClick={() => {
                  resetForm();
                  setShowCreateModal(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="inv-btn-primary"
                disabled={submitting}
                onClick={isEditing ? handleUpdateInvoice : handleCreateInvoice}
              >
                {submitting ? 'Saving...' : isEditing ? 'Update Invoice' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareInvoiceItem && (
        <div className="inv-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="inv-modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3>📲 Share Invoice ({shareInvoiceItem.invoiceNumber || shareInvoiceItem.id})</h3>
              <button className="inv-modal-close" onClick={() => setShowShareModal(false)}>
                ×
              </button>
            </div>

            <div className="inv-modal-body">
              <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #E5E7EB' }}>
                <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>
                  {shareInvoiceItem.project}
                </div>
                <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                  Customer: <strong>{shareInvoiceItem.leadName || leadName || 'Customer'}</strong> • Amount: <strong>{formatCurrency(shareInvoiceItem.amount || 0)}</strong>
                </div>
                {shareInvoiceItem.pdfKey && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FileText size={14} /> PDF is uploaded and ready to share
                  </div>
                )}
              </div>

              {/* Phone for WhatsApp */}
              <div className="inv-form-group" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Phone Number (for WhatsApp Share)
                </label>
                <input
                  type="text"
                  placeholder="+91 9876543210"
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                />
              </div>

              {/* Email for Email Share */}
              <div className="inv-form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Email Address (for Email Share)
                </label>
                <input
                  type="email"
                  placeholder="customer@example.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                />
              </div>

              {/* Share Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* WhatsApp */}
                <button
                  type="button"
                  onClick={handleShareWhatsApp}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: '#25D366',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <MessageSquare size={18} />
                  Share via WhatsApp
                </button>

                {/* Gmail Web */}
                <button
                  type="button"
                  onClick={handleShareGmailWeb}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: '#EA4335',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <Mail size={18} />
                  Open in Gmail Web
                </button>

                {/* System Mail App */}
                <button
                  type="button"
                  onClick={handleShareMailto}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: '#3B82F6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <Mail size={18} />
                  Open System Mail App (Outlook / Mail)
                </button>
              </div>
            </div>

            <div className="inv-modal-footer">
              <button className="inv-btn-secondary" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="inv-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="inv-modal-content inv-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3>Invoice Details</h3>
              <button 
                className="inv-modal-close"
                onClick={() => setShowDetailModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="inv-modal-body">
              <div className="inv-detail-header">
                <div>
                  <h2>{selectedInvoice.invoiceNumber}</h2>
                  <p>{selectedInvoice.project}</p>
                  {selectedInvoice.quoteNumber && (
                    <p style={{ fontSize: '13px', color: '#7C3AED', fontWeight: '600', marginTop: '4px' }}>
                      Originated from Approved Quote: {selectedInvoice.quoteNumber}
                    </p>
                  )}
                </div>
                <div className="inv-detail-statuses">
                  {getStatusBadge(selectedInvoice.status)}
                  {getPaymentStatusBadge(selectedInvoice.paymentStatus)}
                </div>
              </div>

              <div className="inv-detail-grid">
                <div>
                  <label>Customer</label>
                  <p>{selectedInvoice.leadName}</p>
                </div>
                <div>
                  <label>Issue Date</label>
                  <p>{selectedInvoice.issuedDate ? formatDatePDF(selectedInvoice.issuedDate) : '-'}</p>
                </div>
                <div>
                  <label>Due Date</label>
                  <p>{selectedInvoice.dueDate ? formatDatePDF(selectedInvoice.dueDate) : '-'}</p>
                </div>
                {selectedInvoice.paidAmount > 0 && (
                  <div>
                    <label>Paid Amount</label>
                    <p className="inv-paid-amount">{formatCurrency(selectedInvoice.paidAmount)}</p>
                  </div>
                )}
              </div>

              <div className="inv-detail-items">
                <h4>Line Items</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Unit Rate</th>
                      <th>Tax %</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items && selectedInvoice.items.map((item, index) => {
                      const qty = Number(item.quantity || 1);
                      const rate = Number(item.unitPrice || item.amount || 0);
                      const tax = Number(item.tax || selectedInvoice.taxRate || 18);
                      const lineTotal = qty * rate * (1 + tax / 100);

                      return (
                        <tr key={index}>
                          <td>{item.description}</td>
                          <td>{qty}</td>
                          <td>{formatCurrency(rate)}</td>
                          <td>{tax}%</td>
                          <td>{formatCurrency(lineTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="inv-detail-summary">
                <div className="inv-summary-row">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.amount)}</span>
                </div>
                {selectedInvoice.discount > 0 && (
                  <div className="inv-summary-row">
                    <span>Discount ({selectedInvoice.discount}%)</span>
                    <span className="inv-discount">-{formatCurrency((selectedInvoice.amount * selectedInvoice.discount) / 100)}</span>
                  </div>
                )}
                <div className="inv-summary-row inv-summary-total">
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.amount)}</span>
                </div>
              </div>

              {selectedInvoice.paymentInstructions && (
                <div className="inv-detail-terms">
                  <h4>Payment Instructions</h4>
                  <p>{selectedInvoice.paymentInstructions}</p>
                </div>
              )}
              {selectedInvoice.termsAndConditions && (
                <div className="inv-detail-terms" style={{ marginTop: '12px' }}>
                  <h4>Terms and Conditions</h4>
                  <p>{selectedInvoice.termsAndConditions}</p>
                </div>
              )}
            </div>

            <div className="inv-modal-footer">
              <button 
                className="inv-btn-secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </button>
              <button
                className="inv-btn-primary"
                onClick={() => handleViewPDF(selectedInvoice)}
                disabled={generatingPdf}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {generatingPdf ? (
                  <RefreshCw size={16} className="spin" />
                ) : (
                  <FileText size={16} />
                )}
                {selectedInvoice.pdfKey ? 'View PDF' : 'Generate PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      <PdfViewer />
    </div>
  );
}

export default InvoicesTab;